import { fetchWithTimeout } from '@noths/polaris-client-utils';

import { PUBLIC_API_BASE_PATH } from 'src/constants/environment';
import type { TaxonomyPlpNeighborhoodEntry } from 'src/services/contentstack/taxonomyNeighborhood/types';
import { logger } from 'src/utils/serverLogger';
import { encodePathQueryParam } from './encodePathQueryParam';

/**
 * Bounds the request rather than leaving it open indefinitely. `syncTaxonomyContentFromURL`
 * holds `content.syncing` for the whole call, so a hung request would keep the
 * header in its syncing hold forever and keep a browser connection open against
 * a pod that may already be under pressure. The route behind this pages through
 * Contentstack at `TAXONOMY_PLP_LIST_TIMEOUT_MS` (1s) up to `MAX_PAGES` (2), so
 * 3s leaves headroom over the server's own budget.
 */
export const TIMEOUT = 3000;

/** Client-side fetch of the neighbourhood map via the public API route. */
export const fetchNeighborhoodMap = async (
  path: string,
): Promise<TaxonomyPlpNeighborhoodEntry[]> => {
  try {
    const res = await fetchWithTimeout(
      `${PUBLIC_API_BASE_PATH}taxonomy-content-entries?path=${encodePathQueryParam(path)}`,
      { timeout: TIMEOUT },
    );

    if (!res.ok) {
      logger.error('fetchNeighborhoodMap: non-ok response', { path, status: res.status });

      return [];
    }

    const data = (await res.json()) as { entries?: TaxonomyPlpNeighborhoodEntry[] };

    return data.entries ?? [];
  } catch (error) {
    logger.error('fetchNeighborhoodMap: request failed', {
      path,
      message: error instanceof Error ? error.message : String(error),
    });

    return [];
  }
};
